import { Fragment } from "react";
import { LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useRouterState } from "@tanstack/react-router";
import { AppSidebar } from "@/components/AppSidebar";
import { Footer } from "@/components/brand/Footer";
import { HatchBand, hatchBandInset, Version, cn } from "@jbnl/shared-ui";
import { Logo } from "@/components/brand/Logo";
import { kruimelpad, type Kruimel } from "@/components/navigation";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { useSignOut } from "@/features/session/useSignOut";

/** `letterhead` is for pages with nothing to navigate to; `wide` is for the two-pane list-detail. */
export function Shell({
  name,
  letterhead,
  wide,
  children,
}: {
  name?: string;
  letterhead?: boolean;
  wide?: boolean;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();

  // Without navigation the frame is the letterhead instead of the application: mark top-left
  // over its blue rule, hatch band down the right edge.
  if (!name || letterhead) {
    return (
      <div className="relative flex min-h-screen flex-col bg-background">
        <HatchBand />
        <div className="mx-auto w-full max-w-2xl px-6 py-12">
          <div className="mb-8 flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-brand-blue pb-4">
            <Logo className="h-14" />
            <span className="font-bold tracking-[-0.02em]">
              {t("shell.title")}
            </span>
            {name && <Sessie name={name} />}
          </div>
          {children}
          {/* Under the page rather than in the header: on this frame the lockup is the whole
              chrome, and the build is a footnote, not a second thing to read at the top. */}
          <Version className="mt-10" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <SidebarProvider>
      <AppSidebar name={name} />
      <SidebarInset className="bg-sunken">
        {/* Keeps a mostly-white admin screen reading as JBNL rather than as a dashboard. */}
        <HatchBand />
        {/* A pseudo-element carries the bar's surface up a viewport: overscroll would otherwise open a
          gap the viewport-fixed band shows through. */}
        <header
          className={cn(
            "sticky top-0 z-10 flex h-14 shrink-0 items-center gap-3 border-b bg-background px-4",
            "before:pointer-events-none before:absolute before:inset-x-0 before:bottom-full",
            "before:h-screen before:bg-background",
          )}
        >
          <SidebarTrigger />
          <Separator
            orientation="vertical"
            className="data-[orientation=vertical]:h-5"
          />
          <Kruimelpad />
        </header>
        {/* Insets the band rather than the column padding, so the column stays centred. */}
        <div className={hatchBandInset}>
          <main
            className={cn(
              "mx-auto w-full px-6 py-8",
              wide ? "max-w-7xl" : "max-w-5xl",
            )}
          >
            {children}
          </main>
        </div>
        <Footer />
      </SidebarInset>
    </SidebarProvider>
  );
}

/**
 * The group and page are what a collapsed sidebar takes away. No lockup as a first crumb: the
 * sidebar carries the mark, and it would push the changing part off a phone's screen.
 */
function Kruimelpad() {
  const { t } = useTranslation("navigatie");
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const kruimels = kruimelpad(t, pathname);

  if (kruimels.length === 0) return null;

  return (
    <Breadcrumb>
      <BreadcrumbList className="text-sm sm:gap-2">
        {kruimels.map((kruimel, i) => {
          const laatste = i === kruimels.length - 1;

          return (
            // The path is unique per crumb within a trail, and the group heading - the one
            // step without one - is only ever first.
            <Fragment key={kruimel.to ?? kruimel.label}>
              {/* Below md only the page is left, and a trail of one needs no separator. */}
              {i > 0 && <BreadcrumbSeparator className="hidden md:block" />}
              <BreadcrumbItem
                className={cn(!laatste && "hidden md:inline-flex")}
              >
                <Kruimelitem kruimel={kruimel} laatste={laatste} />
              </BreadcrumbItem>
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

/** The current crumb is text, not a link: there is nowhere for it to send you but where you are. */
function Kruimelitem({
  kruimel,
  laatste,
}: {
  kruimel: Kruimel;
  laatste: boolean;
}) {
  if (laatste) {
    return (
      <BreadcrumbPage className="font-bold tracking-[-0.02em]">
        {kruimel.label}
      </BreadcrumbPage>
    );
  }

  if (kruimel.to) {
    return (
      <BreadcrumbLink asChild>
        <Link to={kruimel.to}>{kruimel.label}</Link>
      </BreadcrumbLink>
    );
  }

  return kruimel.label;
}

/** The letterhead has no sidebar, so without this the wrong account is stranded on "Geen toegang". */
function Sessie({ name }: { name: string }) {
  const { t } = useTranslation();
  const signOut = useSignOut();

  // Its own line under the lockup rather than beside it: the two together are wider than the
  // letterhead column, and the mark is not the part that gives way.
  return (
    <div className="flex w-full items-center justify-end gap-3">
      <span className="text-sm text-muted-foreground">{name}</span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => signOut.mutate()}
        disabled={signOut.isPending}
      >
        <LogOut />
        {signOut.isPending ? t("actions.busy") : t("shell.signOut")}
      </Button>
    </div>
  );
}
